import { authFetchBlob } from '@verantos-dev/core-utils';

const ARCHIVE_FILENAME = 'analytical_dataset_results.zip';

const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

interface DownloadAnalyticalDatasetExportInput {
  projectId: string;
  analyticalDatasetProfileId: string;
}

// One in-flight request per profile. Re-mounts of the download modal (StrictMode's
// mount → unmount → mount, or the user closing and reopening it mid-fetch) join the
// running promise instead of firing a duplicate request — and nothing aborts it, so
// closing the modal never cancels the download. Settled entries are evicted so a
// retry starts a fresh request.
const inFlightByProfile = new Map<string, Promise<void>>();

// In-flight changes are observable so UI outside the modal (e.g. the Download
// button) can react — the map is module state, invisible to React on its own.
const inFlightListeners = new Set<() => void>();
const notifyInFlightChange = (): void => inFlightListeners.forEach((listener) => listener());

/** Subscribe to in-flight download changes; returns the unsubscribe function. */
export const subscribeToAdDownloadsInFlight = (listener: () => void): (() => void) => {
  inFlightListeners.add(listener);
  return () => inFlightListeners.delete(listener);
};

/** Whether an export download for this profile is currently in flight. */
export const isAdDownloadInFlight = ({
  projectId,
  analyticalDatasetProfileId,
}: DownloadAnalyticalDatasetExportInput): boolean =>
  inFlightByProfile.has(`${projectId}/${analyticalDatasetProfileId}`);

/**
 * Fetch the analytical dataset's export — a single zip (dataset.csv +
 * data_dictionary.csv) the gateway builds on the fly for the latest PROCESSED
 * run — and save it via a synthetic anchor click. Keyed off the profile (the
 * backend resolves the latest completed run).
 *
 * Deliberately a plain async function, NOT a react-query mutation: the caller
 * fires it from an on-mount effect, and a mutation observer detaches from its
 * in-flight mutation during StrictMode's unsubscribe/re-subscribe and never
 * re-attaches — so the file would download but neither the observer state nor
 * its callbacks would ever report success. Concurrent callers for the same
 * profile share the in-flight promise, so the file saves exactly once.
 */
export const downloadAnalyticalDatasetExport = ({
  projectId,
  analyticalDatasetProfileId,
}: DownloadAnalyticalDatasetExportInput): Promise<void> => {
  const key = `${projectId}/${analyticalDatasetProfileId}`;
  const existing = inFlightByProfile.get(key);
  if (existing) return existing;

  const request = (async () => {
    const blob = await authFetchBlob(
      `/analytical-datasets/${encodeURIComponent(projectId)}` +
        `/analytical-dataset-profile/${encodeURIComponent(analyticalDatasetProfileId)}/download`
    );
    triggerBrowserDownload(blob, ARCHIVE_FILENAME);
  })().finally(() => {
    inFlightByProfile.delete(key);
    notifyInFlightChange();
  });

  inFlightByProfile.set(key, request);
  notifyInFlightChange();
  return request;
};
